import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    MdSecurity,
    MdEmail,
    MdDescription,
    MdErrorOutline,
    MdContentCopy,
    MdCheck,
    MdLockOutline,
} from "react-icons/md";
import epayment from "assets/img/payment/epayment.jpg";

import CommonBanner from "component/Common/CommonBanner";
import useApiClient from "hooks/useApiClient";
import { formatPrice } from "helpers/common";
import { Image } from "react-bootstrap";
import { toast } from "react-toastify";

const STATUS_STYLES = {
    paid: {
        dot: "bg-emerald-500",
        text: "text-emerald-700",
        bg: "bg-emerald-50",
        ring: "ring-emerald-600/10",
    },
    success: {
        dot: "bg-emerald-500",
        text: "text-emerald-700",
        bg: "bg-emerald-50",
        ring: "ring-emerald-600/10",
    },
    pending: {
        dot: "bg-amber-500",
        text: "text-amber-700",
        bg: "bg-amber-50",
        ring: "ring-amber-600/10",
    },
    expired: {
        dot: "bg-red-500",
        text: "text-red-700",
        bg: "bg-red-50",
        ring: "ring-red-600/10",
    },
    failed: {
        dot: "bg-slate-400",
        text: "text-slate-600",
        bg: "bg-slate-100",
        ring: "ring-slate-500/10",
    },
};

const getStatusStyle = (status) =>
    STATUS_STYLES[String(status).toLowerCase()] || STATUS_STYLES.pending;

const PageShell = ({ children, t }) => (
    <div className="min-h-screen bg-slate-50 flex flex-col mb-52">
        <CommonBanner heading={t("paymentLink")} />
        {children}
    </div>
);

const SkeletonCard = () => (
    <section className="flex-1 py-12 px-4 sm:px-6 lg:px-8 mb-50">
        <div className="max-w-2xl mx-auto w-full">
            <div className="bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-8 animate-pulse">
                <div className="flex flex-col items-center pb-6 border-b border-slate-100">
                    <div className="w-12 h-12 rounded-xl bg-slate-100" />
                    <div className="h-4 w-40 bg-slate-100 rounded mt-4" />
                    <div className="h-3 w-24 bg-slate-100 rounded mt-3" />
                </div>
                <div className="grid grid-cols-2 gap-4 py-6">
                    <div className="h-20 bg-slate-100 rounded-xl" />
                    <div className="h-20 bg-slate-100 rounded-xl" />
                    <div className="col-span-2 h-16 bg-slate-100 rounded-xl" />
                </div>
                <div className="h-12 bg-slate-100 rounded-xl" />
            </div>
        </div>
    </section>
);

const PaymentLinkPage = () => {
    const { t, i18n } = useTranslation();
    const { uuid } = useParams();
    const [loading, setLoading] = useState(true);
    const [paymentLink, setPaymentLink] = useState(null);
    const [copied, setCopied] = useState(false);
    const { api, urls } = useApiClient();
    const [isGoing, setIsGoing] = useState(false);

    useEffect(() => {
        if (!uuid) {
            return;
        }

        api.get(`${urls.paymentLink}/${uuid}`)
            .then((res) => {
                if (res.data.error) {
                    throw new Error("Failed to get payment link");
                }
                setPaymentLink(res.data.payment_link);
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, [uuid]);

    const goToPaymentGateway = () => {
        if (isGoing || !paymentLink || !paymentLink?.id) return;
        setIsGoing(true);

        api.post(urls.paymentLinkRegisterOrder, {
            bookingId: paymentLink.id,
            amount: paymentLink.amount,
            currency: "DZD",
            language: i18n.language || "fr",
        })
            .then((res) => {
                if (res?.data?.success && res?.data?.redirect_to) {
                    window.location.href = res?.data?.redirect_to;
                    return;
                }
                throw new Error("can't register order");
            })
            .catch((err) => {
                console.log(err);
                toast.error(t("tryAgainLater"));
            })
            .finally(() => {
                setIsGoing(false);
            });
    };

    const handleCopyLink = async () => {
        if (!paymentLink?.link) return;
        try {
            await navigator.clipboard.writeText(paymentLink.link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) {
        return (
            <PageShell t={t}>
                <SkeletonCard />
            </PageShell>
        );
    }

    if (!paymentLink) {
        return (
            <PageShell t={t}>
                <div className="flex-1 max-w-lg mx-auto w-full px-4 py-20 text-center">
                    <div className="bg-white p-8 rounded-2xl border border-slate-200/80">
                        <div className="w-11 h-11 bg-red-50 text-red-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                            <MdErrorOutline className="w-5 h-5" />
                        </div>
                        <h3 className="text-base font-semibold text-slate-900">
                            {t("paymentLinkNotFound")}
                        </h3>
                        <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
                            {t("paymentLinkNotFoundDesc") ||
                                "The payment link you are looking for is invalid or has expired."}
                        </p>
                    </div>
                </div>
            </PageShell>
        );
    }

    const status = getStatusStyle(paymentLink.status);

    return (
        <PageShell t={t}>
            <section className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto w-full">
                    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] overflow-hidden">
                        <div className="relative px-6 sm:px-8 pt-8 pb-7 text-center bg-gradient-to-b from-slate-50/80 to-white border-b border-slate-100">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-xl mb-4 shadow-sm shadow-blue-600/20">
                                <MdSecurity className="w-6 h-6" />
                            </div>
                            <h2 className="text-lg font-semibold tracking-tight text-slate-900">
                                {t("securePayment")}
                            </h2>
                        </div>

                        <div className="px-6 sm:px-8 py-7 text-center border-b border-slate-100">
                            <span className="block text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">
                                {t("amount")}
                            </span>
                            <div className="text-4xl font-bold text-slate-900 tracking-tight">
                                {formatPrice(paymentLink.amount)}
                            </div>
                            <div
                                className={`inline-flex items-center gap-1.5 mt-4 px-2.5 py-1 rounded-full text-xs font-medium capitalize ring-1 ring-inset ${status.bg} ${status.text} ${status.ring}`}
                            >
                                <span
                                    className={`w-1.5 h-1.5 rounded-full ${status.dot}`}
                                />
                                {t(paymentLink.status)}
                            </div>
                        </div>

                        <div className="px-6 sm:px-8 py-6 space-y-3">
                            {paymentLink.client_email && (
                                <div className="flex items-start gap-3 py-2">
                                    <div className="p-2 bg-slate-50 rounded-lg text-slate-400 shrink-0">
                                        <MdEmail className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0">
                                        <span className="block text-xs font-medium uppercase tracking-wider text-slate-400">
                                            {t("email")}
                                        </span>
                                        <span className="text-sm font-medium text-slate-800 break-all">
                                            {paymentLink.client_email}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-start gap-3 py-2">
                                <div className="p-2 bg-slate-50 rounded-lg text-slate-400 shrink-0">
                                    <MdDescription className="w-4 h-4" />
                                </div>
                                <div className="min-w-0">
                                    <span className="block text-xs font-medium uppercase tracking-wider text-slate-400">
                                        {t("description")}
                                    </span>
                                    <span className="text-sm text-slate-700 leading-relaxed">
                                        {paymentLink.description}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 sm:px-8 pb-8 space-y-3">
                            {paymentLink.status !== "success" && (
                                <button
                                    type="button"
                                    className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-[0.99] text-white text-sm font-semibold py-3.5 px-4 rounded-xl shadow-sm shadow-blue-600/20 transition-all duration-150 cursor-pointer"
                                    onClick={goToPaymentGateway}
                                >
                                    {/* <MdCreditCard className="w-4 h-4" />
                                     */}
                                    <Image
                                        className="w-10 inline-block mr-2"
                                        loading="lazy"
                                        src={epayment}
                                        alt="e-payment"
                                    />
                                    <span>{t("proceedToPayment")}</span>
                                </button>
                            )}

                            {paymentLink.link && (
                                <button
                                    type="button"
                                    onClick={handleCopyLink}
                                    className="w-full flex items-center justify-center gap-2 bg-white hover:bg-slate-50 active:scale-[0.99] text-slate-600 text-sm font-medium py-2.5 px-4 rounded-xl border border-slate-200 transition-all duration-150 cursor-pointer"
                                >
                                    {copied ? (
                                        <>
                                            <MdCheck className="w-4 h-4 text-emerald-600" />
                                            <span className="text-emerald-600">
                                                {t("linkCopied") ||
                                                    "Link copied"}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <MdContentCopy className="w-4 h-4" />
                                            <span>
                                                {t("copyLink") || "Copy link"}
                                            </span>
                                        </>
                                    )}
                                </button>
                            )}

                            <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400 pt-1">
                                <MdLockOutline className="w-3.5 h-3.5" />
                                {t("callSATIM") ||
                                    "Payments are encrypted and processed securely"}
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </PageShell>
    );
};

export default PaymentLinkPage;
